import {t} from '@lingui/macro';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {makeMapDispatchToProps, makeMapStateToProps} from '../../../../../helpers/reselectHelpers';
import {BasicActivityModel} from '../../../../../models/api/activityModel';
import {ActivitySourceVisualTypesEnum} from '../../../../../models/api/activitySourceModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../services/tracking';
import {AppState} from '../../../../../store/appState';
import {ThunkAction} from '../../../../../store/appThunk';
import {getFindTeammate} from '../../../../../store/data/teammates/lazyTeammatesSelectors';
import {getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {mergeConversationsAndNavigateAsyncAction} from '../../../../../store/panels/viewer/viewerPanelConversationsActions';
import {getLocalize} from '../../../../../store/stateless/internationalizationSelectors';
import {navigateToConversation} from '../../../../../store/stateless/navigationActions';
import {SplitConversationBanner} from './splitConversationBanner';

/*
 * Props.
 */

interface SplitConversationBannerContainerProps {
  activity: BasicActivityModel;
}

/*
 * Selectors.
 */

function getBasicActivity(_state: AppState, props: SplitConversationBannerContainerProps) {
  return props.activity;
}

/*
 * Redux.
 */

function makeGetSourceTeammate() {
  return createSelector([getBasicActivity, getFindTeammate], (activity, findTeammate) => {
    const {source} = activity;
    if (source.visualType !== ActivitySourceVisualTypesEnum.TEAMMATE) {
      return undefined;
    }

    return findTeammate(source.teammateId, 'SplitConversationBannerContainer makeGetSourceTeammate');
  });
}

function makeGetSourceTeammateName(getSourceTeammate: ReturnType<typeof makeGetSourceTeammate>) {
  return createSelector([getLocalize, getUserId, getSourceTeammate], (localize, userId, sourceTeammate) => {
    if (!sourceTeammate) {
      return '';
    }

    if (sourceTeammate.id === userId) {
      return localize(t('activity-banner-split-body-you')`You`);
    }

    return buildTeammateDisplayName(sourceTeammate);
  });
}

const mapStateToProps = makeMapStateToProps<SplitConversationBannerContainerProps>()(() => {
  const getSourceTeammate = makeGetSourceTeammate();

  return {
    sourceTeammateName: makeGetSourceTeammateName(getSourceTeammate),
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  onSeeOriginalConversationClick:
    (conversationId: number): ThunkAction =>
    (dispatch) => {
      trackInterfaceEvent(InterfaceEventsEnum.SPLIT_CONVERSATION_BANNER_ACTION, {
        conversation_id: conversationId,
      });

      dispatch(navigateToConversation(conversationId));
    },
  onMergeConversationsClick: mergeConversationsAndNavigateAsyncAction,
}));

export const SplitConversationBannerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SplitConversationBanner);
